import React, { useCallback, useEffect, useState } from "react";
import {
    Sparkles,
    FileText,
    Download,
    RefreshCw,
    Loader2,
    Wand2,
    CheckCircle2,
    AlertCircle,
    Pencil,
    Save,
    X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
    memoriaGet,
    memoriaExtractRequirements,
    memoriaGenerate,
    memoriaRefine,
    memoriaDownloadUrl,
    memoriaSaveContent,
} from "@/lib/api";

function getAuthToken() {
    try {
        return (
            localStorage.getItem("auth_token") ||
            localStorage.getItem("token") ||
            ""
        );
    } catch {
        return "";
    }
}

function ReqRow({ label, value }) {
    if (value === null || value === undefined || value === "") return null;
    if (Array.isArray(value) && value.length === 0) return null;
    let display;
    if (Array.isArray(value)) {
        display = (
            <ul className="list-disc list-inside text-sm text-slate-700 mt-1 space-y-0.5">
                {value.slice(0, 12).map((v, i) => (
                    <li key={i}>
                        {typeof v === "object"
                            ? JSON.stringify(v)
                            : String(v)}
                    </li>
                ))}
            </ul>
        );
    } else {
        display = (
            <span className="font-mono text-slate-900 font-semibold">
                {String(value)}
            </span>
        );
    }
    return (
        <div className="py-2 border-b border-slate-100 last:border-0">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                {label}
            </div>
            <div className="mt-0.5 text-sm">{display}</div>
        </div>
    );
}

function renderMarkdown(md) {
    if (!md) return null;
    const lines = md.split("\n");
    const out = [];
    let listBuffer = [];
    const flushList = () => {
        if (listBuffer.length) {
            out.push(
                <ul
                    key={`l${out.length}`}
                    className="list-disc list-inside text-sm text-slate-700 my-2 space-y-1 ml-2"
                >
                    {listBuffer.map((b, i) => (
                        <li key={i} dangerouslySetInnerHTML={{ __html: b }} />
                    ))}
                </ul>,
            );
            listBuffer = [];
        }
    };
    const inline = (s) =>
        s
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
            .replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, "<em>$1</em>");
    lines.forEach((raw, idx) => {
        const line = raw.trimEnd();
        if (!line) {
            flushList();
            return;
        }
        if (line.startsWith("# ")) {
            flushList();
            out.push(
                <h1
                    key={idx}
                    className="font-display text-2xl font-black mt-4 mb-3 text-slate-900 text-center"
                    dangerouslySetInnerHTML={{ __html: inline(line.slice(2)) }}
                />,
            );
        } else if (line.startsWith("## ")) {
            flushList();
            out.push(
                <h2
                    key={idx}
                    className="font-display text-lg font-bold mt-5 mb-2 text-slate-900"
                    dangerouslySetInnerHTML={{ __html: inline(line.slice(3)) }}
                />,
            );
        } else if (line.startsWith("### ")) {
            flushList();
            out.push(
                <h3
                    key={idx}
                    className="font-display text-base font-semibold mt-4 mb-1 text-slate-800"
                    dangerouslySetInnerHTML={{ __html: inline(line.slice(4)) }}
                />,
            );
        } else if (/^[-*]\s+/.test(line)) {
            listBuffer.push(inline(line.replace(/^[-*]\s+/, "")));
        } else {
            flushList();
            out.push(
                <p
                    key={idx}
                    className="text-sm text-slate-700 leading-relaxed my-2 text-justify"
                    dangerouslySetInnerHTML={{ __html: inline(line) }}
                />,
            );
        }
    });
    flushList();
    return out;
}

export default function MemoriaTecnica({ tenderId, hasPliego }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [extracting, setExtracting] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [refining, setRefining] = useState(false);
    const [refineText, setRefineText] = useState("");
    const [extraInstructions, setExtraInstructions] = useState("");
    const [editMode, setEditMode] = useState(false);
    const [editBuffer, setEditBuffer] = useState("");
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const d = await memoriaGet(tenderId);
            setData(d || {});
        } catch (e) {
            console.error(e);
            setData({});
        } finally {
            setLoading(false);
        }
    }, [tenderId]);

    useEffect(() => {
        load();
    }, [load]);

    // Poll every 4s while a background task is running.
    useEffect(() => {
        if (!data?.generating) return;
        const t = setInterval(async () => {
            try {
                const d = await memoriaGet(tenderId);
                setData(d || {});
                if (!d?.generating) {
                    clearInterval(t);
                    if (d?.error) {
                        toast.error(`Falló: ${d.error}`);
                    } else {
                        toast.success("Memoria técnica lista.");
                    }
                }
            } catch (e) {
                console.error(e);
            }
        }, 4000);
        return () => clearInterval(t);
    }, [data?.generating, tenderId]);

    const handleExtract = async () => {
        if (!hasPliego) {
            toast.error(
                "Sube los pliegos PDF primero (pestaña Documentos).",
            );
            return;
        }
        setExtracting(true);
        // Optimistically flip generating flag so the poller starts even if
        // the POST call times out (Claude can take 20-50 s and Kubernetes
        // ingress may cut the request before the response arrives — the
        // background task still runs server-side).
        setData((prev) => ({ ...(prev || {}), generating: true }));
        toast.success("Analizando pliegos… esto puede tardar 30-60 s.");
        try {
            await memoriaExtractRequirements(tenderId);
        } catch (e) {
            const isTimeout =
                e?.code === "ECONNABORTED" ||
                /timeout/i.test(e?.message || "");
            if (!isTimeout) {
                toast.error(
                    e?.response?.data?.detail ||
                        "No se pudieron extraer las restricciones.",
                );
                setData((prev) => ({ ...(prev || {}), generating: false }));
            }
            // On timeout: silently keep the poller running — the bg task
            // is already underway server-side.
        } finally {
            setExtracting(false);
        }
    };

    const handleGenerate = async () => {
        setGenerating(true);
        setData((prev) => ({ ...(prev || {}), generating: true }));
        toast.success("Redactando la memoria técnica… 30-60 s.");
        try {
            await memoriaGenerate(tenderId, extraInstructions);
            setExtraInstructions("");
        } catch (e) {
            const isTimeout =
                e?.code === "ECONNABORTED" ||
                /timeout/i.test(e?.message || "");
            if (!isTimeout) {
                toast.error(
                    e?.response?.data?.detail ||
                        "No se pudo generar la memoria técnica.",
                );
                setData((prev) => ({ ...(prev || {}), generating: false }));
            } else {
                setExtraInstructions("");
            }
        } finally {
            setGenerating(false);
        }
    };

    const handleRefine = async () => {
        const txt = refineText.trim();
        if (!txt) {
            toast.error("Escribe una instrucción para refinar la memoria.");
            return;
        }
        setRefining(true);
        setData((prev) => ({ ...(prev || {}), generating: true }));
        toast.success("Aplicando cambios… 30-60 s.");
        try {
            await memoriaRefine(tenderId, txt);
            setRefineText("");
        } catch (e) {
            const isTimeout =
                e?.code === "ECONNABORTED" ||
                /timeout/i.test(e?.message || "");
            if (!isTimeout) {
                toast.error(
                    e?.response?.data?.detail ||
                        "No se pudo refinar la memoria.",
                );
                setData((prev) => ({ ...(prev || {}), generating: false }));
            } else {
                setRefineText("");
            }
        } finally {
            setRefining(false);
        }
    };

    const handleDownload = async (format) => {
        try {
            const res = await fetch(memoriaDownloadUrl(tenderId, format), {
                headers: {
                    Authorization: `Bearer ${getAuthToken()}`,
                },
            });
            if (!res.ok) {
                const j = await res.json().catch(() => ({}));
                throw new Error(j.detail || `HTTP ${res.status}`);
            }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `memoria_tecnica.${format}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(e?.message || "No se pudo descargar.");
        }
    };

    const handleStartEdit = () => {
        setEditBuffer(content);
        setEditMode(true);
    };

    const handleCancelEdit = () => {
        setEditMode(false);
        setEditBuffer("");
    };

    const handleSaveEdit = async () => {
        if (!editBuffer.trim()) {
            toast.error("El contenido no puede estar vacío.");
            return;
        }
        setSaving(true);
        try {
            await memoriaSaveContent(tenderId, editBuffer);
            setEditMode(false);
            await load();
            toast.success("Memoria guardada.");
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "No se pudo guardar la memoria.",
            );
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="space-y-3">
                <Skeleton className="h-32 w-full" />
                <Skeleton className="h-64 w-full" />
            </div>
        );
    }

    const requirements = data?.requirements || null;
    const content = data?.content || "";
    const stats = data?.stats || null;

    return (
        <div className="space-y-6" data-testid="memoria-tecnica-pane">
            {/* Step 1: Extract requirements */}
            <div className="rounded-2xl border-2 border-violet-200 bg-violet-50/40 p-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-violet-600">
                            Paso 1
                        </div>
                        <h3 className="font-display text-xl font-semibold mt-1 text-slate-900">
                            Restricciones formales del pliego
                        </h3>
                        <p className="text-sm text-slate-600 mt-1">
                            La IA escanea los pliegos y extrae las
                            restricciones (tipografía, tamaño, número de
                            páginas/palabras, secciones obligatorias…).
                        </p>
                    </div>
                    <Button
                        onClick={handleExtract}
                        disabled={extracting || !hasPliego || !!data?.generating}
                        className="rounded-xl bg-violet-600 hover:bg-violet-700 text-white shrink-0"
                        data-testid="memoria-extract-btn"
                    >
                        {extracting || (data?.generating && !data?.content && !requirements) ? (
                            <Loader2 size={14} className="mr-2 animate-spin" />
                        ) : (
                            <Sparkles size={14} className="mr-2" />
                        )}
                        {data?.generating && !requirements
                            ? "Extrayendo…"
                            : requirements
                              ? "Re-extraer restricciones"
                              : "Extraer restricciones"}
                    </Button>
                </div>
                {!hasPliego && (
                    <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 inline-flex items-center gap-2">
                        <AlertCircle size={13} />
                        Sube los pliegos PDF primero en la pestaña{" "}
                        <b>Documentos</b>.
                    </div>
                )}
                {requirements && (
                    <div
                        className="mt-4 bg-white rounded-xl p-4 border border-violet-100"
                        data-testid="memoria-requirements-box"
                    >
                        <ReqRow label="Máx. páginas" value={requirements.max_pages} />
                        <ReqRow label="Máx. palabras" value={requirements.max_words} />
                        <ReqRow
                            label="Máx. caracteres"
                            value={requirements.max_characters}
                        />
                        <ReqRow label="Tipografía" value={requirements.font_family} />
                        <ReqRow
                            label="Tamaño de letra"
                            value={requirements.font_size_pt}
                        />
                        <ReqRow
                            label="Interlineado"
                            value={requirements.line_spacing}
                        />
                        <ReqRow label="Márgenes (cm)" value={requirements.margin_cm} />
                        <ReqRow label="Tamaño de página" value={requirements.page_size} />
                        <ReqRow
                            label="Formato de entrega"
                            value={requirements.submission_format}
                        />
                        <ReqRow
                            label="Secciones obligatorias"
                            value={requirements.sections_required}
                        />
                        <ReqRow
                            label="Criterios puntuables"
                            value={(requirements.criteria_weights || []).map(
                                (c) => `${c.name}: ${c.weight_pct}%`,
                            )}
                        />
                        <ReqRow label="Notas adicionales" value={requirements.notes} />
                        {data?.requirements_extracted_at && (
                            <div className="mt-3 text-[10px] text-slate-400 font-mono">
                                Extraído:{" "}
                                {new Date(
                                    data.requirements_extracted_at,
                                ).toLocaleString("es-ES")}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Step 2: Generate */}
            <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50/40 p-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-600">
                            Paso 2
                        </div>
                        <h3 className="font-display text-xl font-semibold mt-1 text-slate-900">
                            Generar la memoria técnica
                        </h3>
                        <p className="text-sm text-slate-600 mt-1">
                            Claude redacta una memoria respetando las
                            restricciones anteriores y los datos de la
                            solvencia de la empresa.
                        </p>
                    </div>
                    <Button
                        onClick={handleGenerate}
                        disabled={generating || !hasPliego || !!data?.generating}
                        className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white shrink-0"
                        data-testid="memoria-generate-btn"
                    >
                        {generating || (data?.generating && requirements) ? (
                            <Loader2 size={14} className="mr-2 animate-spin" />
                        ) : (
                            <FileText size={14} className="mr-2" />
                        )}
                        {data?.generating && requirements
                            ? "Redactando…"
                            : content
                              ? "Regenerar desde cero"
                              : "Generar memoria técnica"}
                    </Button>
                </div>
                {!content && (
                    <Textarea
                        value={extraInstructions}
                        onChange={(e) => setExtraInstructions(e.target.value)}
                        placeholder="(Opcional) Indicaciones específicas para la primera redacción. Ej: 'Enfatiza nuestra experiencia en proyectos similares de obra pública'"
                        className="mt-3 rounded-xl bg-white border-emerald-200"
                        rows={2}
                        data-testid="memoria-extra-instructions"
                    />
                )}
            </div>

            {/* Step 3: Preview + Refine + Download */}
            {content && (
                <>
                    <div className="rounded-2xl bg-white border-2 border-slate-200 overflow-hidden">
                        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap bg-slate-50/50">
                            <div className="flex items-center gap-2">
                                <CheckCircle2
                                    size={16}
                                    className="text-emerald-500"
                                />
                                <span className="font-display text-base font-semibold">
                                    Vista previa
                                </span>
                                {stats && (
                                    <span className="text-xs text-slate-500 font-mono ml-2">
                                        ≈ {stats.words?.toLocaleString("es-ES")}{" "}
                                        palabras · {stats.characters?.toLocaleString("es-ES")} car. · {stats.approx_pages} pág.
                                    </span>
                                )}
                            </div>
                            <div className="flex gap-2">
                                {editMode ? (
                                    <>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="rounded-xl"
                                            onClick={handleCancelEdit}
                                            disabled={saving}
                                            data-testid="memoria-cancel-edit-btn"
                                        >
                                            <X size={12} className="mr-1.5" />
                                            Cancelar
                                        </Button>
                                        <Button
                                            size="sm"
                                            className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white"
                                            onClick={handleSaveEdit}
                                            disabled={saving}
                                            data-testid="memoria-save-edit-btn"
                                        >
                                            {saving ? (
                                                <Loader2 size={12} className="mr-1.5 animate-spin" />
                                            ) : (
                                                <Save size={12} className="mr-1.5" />
                                            )}
                                            Guardar cambios
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="rounded-xl"
                                            onClick={handleStartEdit}
                                            disabled={!!data?.generating}
                                            data-testid="memoria-edit-btn"
                                        >
                                            <Pencil size={12} className="mr-1.5" />
                                            Editar
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="rounded-xl"
                                            onClick={() => handleDownload("docx")}
                                            data-testid="memoria-download-docx-btn"
                                        >
                                            <Download size={12} className="mr-1.5" />
                                            Word
                                        </Button>
                                        <Button
                                            size="sm"
                                            className="rounded-xl bg-[#0055ff] hover:bg-[#0044cc] text-white"
                                            onClick={() => handleDownload("pdf")}
                                            data-testid="memoria-download-pdf-btn"
                                        >
                                            <Download size={12} className="mr-1.5" />
                                            PDF
                                        </Button>
                                    </>
                                )}
                            </div>
                        </div>
                        {editMode ? (
                            <div className="p-5">
                                <div className="text-[11px] text-slate-500 mb-2 font-mono">
                                    Markdown · usa <code>#</code> para H1, <code>##</code> H2, <code>###</code> H3, <code>-</code> listas, <code>**negrita**</code>.
                                </div>
                                <Textarea
                                    value={editBuffer}
                                    onChange={(e) => setEditBuffer(e.target.value)}
                                    className="font-mono text-sm bg-white border-slate-200 rounded-xl min-h-[600px]"
                                    rows={30}
                                    data-testid="memoria-edit-textarea"
                                />
                                <div className="mt-2 text-[11px] text-slate-400 font-mono">
                                    {editBuffer.length.toLocaleString("es-ES")} caracteres · {(editBuffer.match(/\S+/g) || []).length.toLocaleString("es-ES")} palabras
                                </div>
                            </div>
                        ) : (
                            <div
                                className="p-8 max-h-[700px] overflow-y-auto"
                                data-testid="memoria-content-preview"
                            >
                                {renderMarkdown(content)}
                            </div>
                        )}
                    </div>

                    {/* Refine */}
                    <div className="rounded-2xl border-2 border-amber-200 bg-amber-50/40 p-5">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-amber-600">
                            Paso 3 (opcional)
                        </div>
                        <h3 className="font-display text-xl font-semibold mt-1 text-slate-900">
                            Refinar con instrucciones
                        </h3>
                        <p className="text-sm text-slate-600 mt-1">
                            Pídele a la IA cambios concretos. Ejemplo:
                            "házla más corta", "elimina la sección de
                            riesgos", "añade un apartado sobre ODS".
                        </p>
                        <div className="flex gap-2 mt-3">
                            <Textarea
                                value={refineText}
                                onChange={(e) => setRefineText(e.target.value)}
                                placeholder="Escribe tu instrucción en lenguaje natural…"
                                className="rounded-xl bg-white border-amber-200 flex-1"
                                rows={2}
                                data-testid="memoria-refine-input"
                            />
                            <Button
                                onClick={handleRefine}
                                disabled={refining || !refineText.trim() || !!data?.generating}
                                className="rounded-xl bg-amber-600 hover:bg-amber-700 text-white self-stretch px-5"
                                data-testid="memoria-refine-btn"
                            >
                                {refining || data?.generating ? (
                                    <Loader2 size={14} className="animate-spin" />
                                ) : (
                                    <Wand2 size={14} />
                                )}
                            </Button>
                        </div>
                        <div className="mt-3 flex gap-2 flex-wrap">
                            {[
                                "Hazla un 30% más corta",
                                "Hazla un 40% más larga",
                                "Elimina la sección de mejoras adicionales",
                                "Reescribe con un tono más comercial",
                            ].map((q) => (
                                <button
                                    key={q}
                                    type="button"
                                    onClick={() => setRefineText(q)}
                                    className="text-[11px] font-medium text-amber-700 bg-amber-100 hover:bg-amber-200 border border-amber-300 rounded-full px-2.5 h-6 transition-all"
                                >
                                    {q}
                                </button>
                            ))}
                            <button
                                type="button"
                                onClick={() => load()}
                                className="text-[11px] font-medium text-slate-500 hover:text-slate-700 inline-flex items-center gap-1 ml-auto"
                            >
                                <RefreshCw size={11} />
                                Recargar
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
